/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['modal'] }] */
import React, { useCallback, useState } from 'react';
import cn from 'classnames';

import { evSearchCrossSiteRecoClick } from 'events/recommendations';
import { track } from 'apis/amethyst';
import ImageCarousel from 'components/common/ImageCarousel';
import CrossSiteLabel from 'components/common/melodyCard/CrossSiteLabel';
import TrustedRetailerBanner from 'components/common/TrustedRetailerBanner';
import useMartyContext from 'hooks/useMartyContext';
import { constructMSAImageUrl } from 'helpers';
import { LazyImage } from 'components/common/Image';

import { header } from 'styles/components/common/modal.scss';
import css from 'styles/components/search/crossSiteProducts.scss';

const PRODUCT_IMAGE_SIZE = 450;
const MSA_IMAGE_DIMENSION = { width: PRODUCT_IMAGE_SIZE, height: PRODUCT_IMAGE_SIZE };

const useCrossSite = (product, fetchProductDetail) => {
  const { testId, marketplace: { shortName } } = useMartyContext();
  const [data, setData] = useState(null);
  const [details, setDetails] = useState(false);

  const handleDetailsClick = useCallback(details => {
    setDetails(!details);
  }, []);

  const handleClickCard = useCallback(e => {
    e.preventDefault();
    track(() => ([
      evSearchCrossSiteRecoClick, { product }
    ]));
    fetchProductDetail(product, { includeOos: true, errorOnOos: false }).then(response => {
      const respData = { ...product, ...response };
      setData(respData);
    });
  }, [product, fetchProductDetail]);

  const handleClickStay = useCallback(product => {
    track(() => ([
      evSearchCrossSiteRecoClick, { product, proceedToTrustedRetailer: false }
    ]));
    setData(null);
  }, []);

  const handleClickContinue = useCallback(product => {
    track(() => ([
      evSearchCrossSiteRecoClick, { product, proceedToTrustedRetailer: true }
    ]));
    setData(null);
  }, []);

  const makeProductImage = () => {
    const imageUrls = data.styles.find(v => v.styleId === data.styleId) || { images: [{ imageId: data.thumbnailImageUrl }] };
    const images = imageUrls.images.map((i, k) => <LazyImage key={k} src={constructMSAImageUrl(i.imageId, MSA_IMAGE_DIMENSION)} alt={i.imageId} />);
    return <ImageCarousel
      slidesToScroll={1}
      slidesToShow={1}
      autoplay={false}
      wrapAround={true}
      overlapDots={true}>
      {images}
    </ImageCarousel>;
  };

  const makeContent = () => {
    if (!data) {
      return null;
    }

    const { storeUrl, styleId, storeName, productSeoUrl, productName, brandName, price } = data;

    return (
      <>
        <section className={css.modalCarousel}>{makeProductImage(data)}</section>
        <section className={css.modalContent}>
          <div className={cn(header, css.header)}>
            <ul>
              {storeName && <li><CrossSiteLabel storeName={storeName}/></li>}
              <li className={css.trustedRetailer}><TrustedRetailerBanner styleId={styleId}/></li>
            </ul>
          </div>
          <h2>Now leaving {shortName}</h2>
          <div>
            <p className={css.copy}>
              We don't carry this product, but <a href={storeUrl}>{`${storeName}.com`}</a> does! We like them. We think you will too!
            </p>
            <button
              className={css.trustedInfo}
              aria-expanded={!!details}
              onClick={() => handleDetailsClick(details)}
              type="button">Why are they a Trusted Retailer?</button>
            {details && (
              <ul>
                <li><strong>Reputable Business:</strong> This is a Zappos sister site that carries genuine products.</li>
                <li><strong>Use your Zappos Login:</strong> Nothing new to remember, just use your same Zappos username and password.</li>
                <li><strong>Great customer benefits:</strong> Friend customer service, 2 day free shipping, 30 day return policy.</li>
              </ul>
            )}
          </div>
          <div className={css.modalBtnContainer}>
            <button
              data-test-id={testId('searchResultsCrossSiteModalStay')}
              type="button"
              className={css.btnStay}
              onClick={() => handleClickStay(data)}>
              Stay on {shortName}
            </button>
            <a
              onClick={() => handleClickContinue(data)}
              data-test-id={testId('searchResultsCrossSiteModalContinue')}
              className={css.btnContinue}
              href={productSeoUrl}
              target="_blank"
              rel="noopener noreferrer">
              Continue to {storeName}
            </a>
          </div>
          <div className={css.btnWrapper}>
            {brandName && <p>{brandName}</p>}
            {productName && <p className={css.productName}>{productName}</p>}
            {price && <p className={css.price}>{price}</p>}
          </div>
        </section>
      </>
    );
  };

  return { onClick: product.storeName ? handleClickCard : null, closeClick: handleClickStay, content: makeContent(), visible: !!data };
};

export default useCrossSite;
